define([
	"dojo/_base/declare",
	"csi",
	"csi/def",
	"csi/def/Action",
	"csi/def/Heading",
	"csi/locale",
	"csi/reflection",
	"csi/util",
	"csi/xml"
], function(declare, csi, csiDef, Action, Heading, locale, reflection, util, xml) {

	return declare(Action, {
		// String: default tag for action
		m_tag: "Aggregate",

		// String: relationship holding selections
		aggregateId: null,

		// String: class to create to wrap aggregate node(s)
		aggregateClass: null,

		// String[]: array of relationship ids from AggregateClass to aggregate node
		aggregateClassIds: null,

		// Function: extension point for checking if existing selection should be treated as such (if not, it could be selected again)
		//		Boolean function(/*Context*/ctx, /*Node*/child)
		aggregateChildTest: null,

		// Function: extension point for checking if node can be selected
		//		Boolean function(/*Context*/ctx, /*FormData*/fd)
		aggregateTest: null,

		// Boolean: allow nodes to be aggregated multiple times (must be used in combination with aggregateClass)
		bAggregateAdd: null,

		// Boolean: only allow selection of nodes that are not yet part of relationship
		bAggregateNew: null,

		// Boolean: indicates if all changes should be submitted in one request or individually
		bOneRequest: false,

		// Boolean: indicates if action node is at source(false) or detination(true) of relationship (not used with aggregate stores)
		bReverse: false,

		// Heading: Node Heading used to get action view
		heading: null,

		// String: optional form on AggregateClass to show for each selected node
		idForm: null,

		/** @property {C8AttributeId} reverseAggregateId - id of reverse relationship to which this node will be added */
		reverseAggregateId: null,

		/** @property {C8NodeType} reverseType - type of source class owning relationship that can referencing the target class of this action */
		reverseType: null,

		constructor(def) {
			this.aggregateId = util.getProperty(def, "AggregateId");
			this.idForm = util.getProperty(def, "Form");
			this.reverseAggregateId = util.getProperty(def, "ReverseAggregateId");
			this.reverseType = util.getProperty(def, "ReverseType");

			// deprecated with use of aggregate stores
			this.aggregateClass = util.getProperty(def, "AggregateClass");
			this.aggregateChildTest = util.getProperty(def, "AggregateChildTest");
			this.aggregateTest = util.getProperty(def, "AggregateTest");
			this.bAggregateAdd = (util.getProperty(def, "AggregateAdd") == "1");
			this.bAggregateNew = (util.getProperty(def, "AggregateNew") == "1");
			this.bOneRequest = this.m_request && (util.getProperty(def, "OneRequest") == "1");
			this.bReverse = (util.getProperty(def, "Reverse") == "1");

			// AggregateClassId can be comma-separated list
			var ids = util.getProperty(def, "AggregateClassId");
			if (ids) {
				this.aggregateClassIds = ids.split(",");
			}

			if (!this.bReverse && (this.idForm == null) && this.aggregateClass) {
				this.idForm = csiDef.DEFAULT_FORM_ID;
			}
		},

		getId() {
			return this.m_id || (this.m_tag + this.aggregateId);
		},

		/**
		 * @override
		 * Test for invalid conditions
		 */
		isValid() {
			const {m_id, idForm, bReverse, reverseAggregateId, reverseType} = this;

			// if using reverse aggregate store, then ReverseAggregateId, ReverseType, and Id must all be specified
			if (reverseAggregateId && (!m_id || !reverseType)) {
				return false;
			}

			// a form should not be specified with a reverse aggregation (cannot be composite)
			if (idForm && (bReverse || reverseAggregateId)) {
				return false;
			}

			return this.inherited(arguments);
		},

		resolve: function(/*LocaleResolver*/lr, /*Class*/rcRoot, /*Node*/nodeRoot) {
			// summary: resolve heading to be used for action view
			this.inherited(arguments);

			const {
				aggregateClass,
				aggregateClassIds,
				aggregateId,
				bReverse,
				heading,
				idForm,
				reverseAggregateId,
				m_rcTarget
			} = this;

			// reverse aggregations are not bidirectional, no heading to create from the target class of this action;
			// they also cannot be composite, so no form to use
			if (reverseAggregateId || bReverse || !aggregateId) {
				return;
			}

			// check for target class
			if (!m_rcTarget) {
				console.warn(`AggregateBase - target class is unknown for relationship "${aggregateId}"`);
				return;
			}

			// find relation for action
			const rca = m_rcTarget.getRelation(aggregateId, true);
			if (aggregateId && !rca) {
				console.warn(`AggregateBase - relationship "${aggregateId}" does not exist on type "${m_rcTarget.type}"`);
				return;
			}

			// if composite, then we should try to use the default form for the class if one is not specified explicitly
			if (aggregateClass || rca?.isComposite) {
				this.idForm = (idForm == null) ? csiDef.DEFAULT_FORM_ID : idForm;
			} else if (idForm) {
				console.warn(`AggregateBase - Form cannot be used when relationship "${aggregateId}" is not composite`);
				this.idForm = null;
			}

			// find heading for action
			if (!heading) {
				const rc = aggregateClass ? reflection.getClass(aggregateClass) : m_rcTarget;
				let classHeading;

				if (aggregateClass) {
					classHeading = csi.transformOne(aggregateClassIds, (id) => csiDef.getClassHeading(rc, id));
				} else {
					classHeading = csiDef.getClassHeading(rc, aggregateId);
				}

				this.heading = new Heading(classHeading?.[0]);
				this.heading.m_id = this.heading.m_id || aggregateClassIds?.[0] || aggregateId;
				this.heading.resolveHeading(locale.localeResolver, null, rc);
			}
		},

		_checkAccess: function(/*Node*/node, /*String*/right, /*String*/rightBase) {
			// summary: make sure the attribute is enabled and editable unless reverse aggregation
			const {aggregateId, bReverse, reverseAggregateId} = this;
			const rca = reflection.getAttribute(node, aggregateId);

			if (!aggregateId || bReverse || reverseAggregateId || rca?.isReverseRelation) {
				return xml.nodeHasRight(node, right);
			}

			if (rca?.isEditingDisabled) {
				return false;
			}

			return xml.nodeHasAccess(node, right, aggregateId) && xml.nodeHasRight(node, rightBase);
		},

		_getNameParams: function(/*csi.LocaleResolver*/lr) {
			// summary: return array of parameters used to help name action
			const {aggregateId, bReverse, m_rcTarget, reverseType} = this;

			if (reverseType) {
				return [lr.getTypePlural(reverseType)];
			} else if (m_rcTarget && !bReverse) {
				return [lr.getTypeAttribute(m_rcTarget.getType(), aggregateId)];
			}

			return null;
		}
	});
});